/**
 * HealthMetricsInput Component
 *
 * Content-only component for entering daily health metric values during
 * the pre-trading ritual. Displays configured metrics from PreTradingSettings
 * and allows users to enter today's values.
 *
 * This component renders just the inputs (no Card wrapper) - the parent
 * component is responsible for providing the Card chrome.
 *
 * This is different from HealthMetricsCard (settings) which configures
 * WHAT metrics exist - this component lets users enter TODAY'S values.
 */

import React, { useCallback } from 'react';
import { NumberInput, EmptyState } from '@enfomentis/ui';

/**
 * Health metric configuration from PreTradingSettings
 */
export interface HealthMetricConfig {
  id: string;
  name: string;
  min: number;
  max: number;
  unit?: string;
}

/**
 * Props for HealthMetricsInput component
 */
export interface HealthMetricsInputProps {
  /** Configured metrics from PreTradingSettings.healthMetrics.metrics */
  metrics: HealthMetricConfig[];
  /** Current values (metric id → value, undefined = unset) */
  values: Record<string, number | undefined>;
  /** Callback when values change */
  onValuesChange: (values: Record<string, number | undefined>) => void;
}

/**
 * Normalize HeroUI NumberInput value to our expected type.
 * NumberInput's onValueChange can return number | number[] | null,
 * but we only expect single number values for health metrics.
 * Arrays are treated as undefined to avoid incorrect writes if range mode is ever introduced.
 */
const normalizeValue = (
  val: number | number[] | null | undefined
): number | undefined => {
  if (val === null || val === undefined) return undefined;
  if (Array.isArray(val)) return undefined; // Treat arrays as unset (health metrics don't use range mode)
  if (Number.isNaN(val)) return undefined; // Guard against NaN values
  return val;
};

/**
 * Health Metrics Input Component
 *
 * Renders NumberInput controls for each configured health metric.
 * Shows an empty state when no metrics are configured.
 *
 * Note: This is a content-only component. Parent provides the Card wrapper.
 */
export const HealthMetricsInput: React.FC<HealthMetricsInputProps> = ({
  metrics,
  values,
  onValuesChange,
}) => {
  /**
   * Handle value change for a specific metric
   */
  const handleValueChange = useCallback(
    (metricId: string, rawValue: number | number[] | null | undefined) => {
      const newValue = normalizeValue(rawValue);
      onValuesChange({
        ...values,
        [metricId]: newValue,
      });
    },
    [values, onValuesChange]
  );

  // Empty state when no metrics configured
  if (metrics.length === 0) {
    return (
      <EmptyState
        title="No health metrics configured"
        description="Configure health metrics in Settings to track your daily wellness"
      />
    );
  }

  // Metrics input list
  return (
    <div className="space-y-4">
      {metrics.map((metric) => {
        const currentValue = values[metric.id];
        return (
          <div key={metric.id} className="w-full">
            <NumberInput
              label={metric.name}
              value={currentValue}
              onValueChange={(val) => handleValueChange(metric.id, val)}
              minValue={metric.min}
              maxValue={metric.max}
              placeholder={`${metric.min}–${metric.max}`}
              endContent={
                metric.unit ? (
                  <span className="text-text-tertiary text-sm">
                    {metric.unit}
                  </span>
                ) : undefined
              }
              hideStepper
              classNames={{
                input: 'text-base',
                inputWrapper: 'min-h-[44px]',
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default HealthMetricsInput;
